import { useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Download, Warehouse as WarehouseIcon, Edit2, Plus, X, History } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { WarehouseForm } from '@/features/merchants/tabs/WarehouseForm';
import { WarehouseHistoryDialog } from './WarehouseHistoryDialog';
import { GmailPagination } from '@/components/ui/GmailPagination';

interface Warehouse {
  id: number;
  name: string;
  code?: string;
  contactName?: string;
  contactPhone?: string;
  merchantId?: number;
  merchant?: { id: number; companyName: string; brandName?: string } | null;
  city?: string;
  state?: string;
  stateId?: number;
  pincode?: string;
  rtoMobile?: string;
  rtoCity?: string;
  rtoState?: string;
  rtoPincode?: string;
  isActive: boolean;
  isMaster: boolean;
  address?: string;
  rtoAddress?: string;
  rtoStateId?: number;
  /** Who last changed this record. Sent by the API to INTERNAL callers only — never to a merchant. */
  lastEdit?: { by: string | null; at: string; type: string; action: string } | null;
}

interface Merchant { id: number; companyName: string }
interface State { id: number; name: string }

export function WarehousesPage() {
  const [search, setSearch] = useState('');
  const [merchantId, setMerchantId] = useState<string>('');
  const { user: _au } = useAuth();
  const isMerchant = _au?.kind === 'merchant';
  const [stateId, setStateId] = useState<string>('');
  const [isActive, setIsActive] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  // Admin-side "add a pickup location for a merchant". Two steps: choose the merchant, then fill the
  // same form the merchant's own page uses — so there is exactly one create path, and creating here
  // registers the location with Delhivery (B2C + B2B) and Ekart through the usual fan-out.
  const [addOpen, setAddOpen] = useState(false);
  // Editing happens HERE now. It used to be a link to the merchant's profile, which threw the
  // operator onto a different screen, with different filters, to change one address — and then left
  // them to find their way back to the list they were working through.
  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [history, setHistory] = useState<Warehouse | null>(null);
  // This screen used to load ALL 5,725 pickup locations with every column — ~3.95 MB on one request,
  // the single biggest thing this server sent. A page of 50 is ~35 KB.
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [addMerchantId, setAddMerchantId] = useState<number | null>(null);
  const [merchantQuery, setMerchantQuery] = useState('');
  const qc = useQueryClient();

  const { data: merchants } = useQuery({
    queryKey: ['merchants', 'all-for-filter'],
    queryFn: async () => (await api.get<Merchant[]>('/merchants')).data,
    staleTime: 1000 * 60 * 5,
  });
  const { data: states } = useQuery({
    queryKey: ['states'],
    queryFn: async () => (await api.get<State[]>('/states')).data,
    staleTime: 1000 * 60 * 60,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['warehouses', { search, merchantId, stateId, isActive, page }],
    queryFn: async () => {
      const params: Record<string, string> = { page: String(page), pageSize: String(PAGE_SIZE) };
      if (search) params.search = search;
      if (merchantId) params.merchantId = merchantId;
      if (stateId) params.stateId = stateId;
      if (isActive) params.isActive = isActive;
      // Asking for a page changes the SHAPE to { items, total, ... }. Callers that do not ask still
      // get a plain array, which is what keeps the pickup-request dropdown working untouched.
      return (await api.get<{ items: Warehouse[]; total: number }>('/warehouses', { params })).data;
    },
    // Filters narrow the set, so page 3 of the old filter is meaningless under the new one.
    placeholderData: (prev) => prev,
  });
  const warehouses = data?.items;
  const total = data?.total ?? 0;

  const merchantOptions = useMemo(() => {
    const q = merchantQuery.trim().toLowerCase();
    const all = merchants ?? [];
    if (!q) return all.slice(0, 50);
    return all.filter(m => m.companyName?.toLowerCase().includes(q) || String(m.id) === q).slice(0, 50);
  }, [merchants, merchantQuery]);

  const closeAdd = () => { setAddOpen(false); setAddMerchantId(null); setMerchantQuery(''); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: Record<string, string> = {};
      if (merchantId) params.merchantId = merchantId;
      if (stateId) params.stateId = stateId;
      const r = await api.get('/warehouses/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `warehouses-all-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally { setExporting(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Merchant Pickup Locations</h1>
          <p className="text-slate-500 text-sm mt-1">All merchant pickup locations across the platform</p>
        </div>
        <div className="flex items-center gap-2">
          {!isMerchant && (
            <button onClick={() => { setAddOpen(true); setAddMerchantId(merchantId ? Number(merchantId) : null); }}
              className="px-3 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:opacity-90 flex items-center gap-2">
              <Plus size={14} /> Add Pickup Location
            </button>
          )}
          <button onClick={handleExport} disabled={exporting}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg font-medium hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
            <Download size={14} /> {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>

      {addOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto py-10" onClick={closeAdd}>
          <div className="bg-white rounded-xl w-full max-w-3xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <h2 className="font-semibold text-slate-900">Add Pickup Location</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Saving registers this address with Delhivery (B2C and B2B) and Ekart automatically.
                </p>
              </div>
              <button onClick={closeAdd} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>

            <div className="p-5">
              {/* Step 1 — which merchant does this pickup location belong to? */}
              <label className="block text-xs font-medium text-slate-600 mb-1">Merchant</label>
              {addMerchantId ? (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 px-3 py-2 rounded-lg border border-slate-300 bg-slate-50 text-sm">
                    #{addMerchantId} · {merchants?.find(m => m.id === addMerchantId)?.companyName ?? 'Selected merchant'}
                  </div>
                  <button onClick={() => { setAddMerchantId(null); setMerchantQuery(''); }}
                    className="text-xs text-primary hover:underline whitespace-nowrap">Change</button>
                </div>
              ) : (
                <div className="mb-4">
                  <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-300">
                    <Search size={15} className="text-slate-400" />
                    <input autoFocus value={merchantQuery} onChange={(e) => setMerchantQuery(e.target.value)}
                      placeholder="Search merchant by name or id…"
                      className="flex-1 bg-transparent outline-none text-sm" />
                  </div>
                  <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {merchantOptions.length === 0 && (
                      <div className="px-3 py-3 text-sm text-slate-400">No merchant matches “{merchantQuery}”.</div>
                    )}
                    {merchantOptions.map(m => (
                      <button key={m.id} onClick={() => setAddMerchantId(m.id)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                        <span className="text-slate-400 font-mono text-xs mr-2">#{m.id}</span>{m.companyName}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2 — the same form the merchant's own Pickup Locations page uses. */}
              {addMerchantId
                ? <WarehouseForm merchantId={addMerchantId}
                    onSuccess={() => { qc.invalidateQueries({ queryKey: ['warehouses'] }); closeAdd(); }} />
                : <p className="text-sm text-slate-400">Pick a merchant to continue.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Edit, in place. The SAME form the merchant's own page and the Add flow use, so there is one
          definition of what a pickup location is and one path that registers it with the couriers. */}
      {editing && (
        <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div>
                <h3 className="font-semibold text-slate-800">Edit pickup location</h3>
                <p className="text-xs text-slate-500">
                  #{editing.id} · {editing.name}
                  {editing.merchant ? ` · ${editing.merchant.companyName}` : ''}
                </p>
              </div>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5">
              <WarehouseForm
                merchantId={editing.merchantId!}
                warehouseId={editing.id}
                defaultValues={{
                  name: editing.name, code: editing.code, contactName: editing.contactName,
                  contactPhone: editing.contactPhone, address: editing.address,
                  city: editing.city, stateId: editing.stateId as any, pincode: editing.pincode,
                  rtoMobile: editing.rtoMobile, rtoAddress: editing.rtoAddress,
                  rtoCity: editing.rtoCity, rtoStateId: editing.rtoStateId as any, rtoPincode: editing.rtoPincode,
                  isActive: editing.isActive,
                } as any}
                onSuccess={() => { qc.invalidateQueries({ queryKey: ['warehouses'] }); setEditing(null); }}
              />
            </div>
          </div>
        </div>
      )}

      <WarehouseHistoryDialog warehouse={history} onClose={() => setHistory(null)} />

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="flex-1 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 min-w-[260px]">
            <Search size={16} className="text-slate-400" />
            <input type="text" placeholder="Search warehouse, code, city, contact..."
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          {!isMerchant && (
          <select value={merchantId} onChange={(e) => { setMerchantId(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary min-w-[200px]">
            <option value="">All merchants</option>
            {merchants?.map(m => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}
          </select>
          )}
          <select value={stateId} onChange={(e) => { setStateId(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary min-w-[180px]">
            <option value="">All states</option>
            {states?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={isActive} onChange={(e) => { setIsActive(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary">
            <option value="">Any status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Warehouse</th>
                <th className="text-left px-4 py-3">Merchant</th>
                <th className="text-left px-4 py-3">City / State</th>
                <th className="text-left px-4 py-3">Pincode</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">RTO</th>
                <th className="text-left px-4 py-3">Status</th>
                {/* Internal only — a merchant has no business seeing which of our staff touched it. */}
                {!isMerchant && <th className="text-left px-4 py-3">Last updated</th>}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="text-center py-8 text-slate-400">Loading...</td></tr>}
              {!isLoading && (warehouses?.length ?? 0) === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-slate-400">
                  <WarehouseIcon size={20} className="mx-auto mb-1 opacity-50" />
                  No warehouses match these filters.
                </td></tr>
              )}
              {warehouses?.map((w) => (
                <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-500 text-xs">#{w.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{w.name}</div>
                    {w.code && <div className="text-xs"><code className="bg-slate-100 px-1.5 py-0.5 rounded">{w.code}</code></div>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {w.merchant
                      ? <Link to={`/merchants/${w.merchantId}`} className="text-primary hover:underline">{w.merchant.companyName}</Link>
                      : <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700">Master</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{[w.city, w.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs font-mono">{w.pincode ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {w.contactName ?? '—'}{w.contactPhone ? <div className="text-slate-400">{w.contactPhone}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {w.rtoCity || w.rtoMobile
                      ? <>{[w.rtoCity, w.rtoState, w.rtoPincode].filter(Boolean).join(', ')}{w.rtoMobile && <div className="text-slate-400">{w.rtoMobile}</div>}</>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${w.isActive ? 'text-success' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${w.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                      {w.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {!isMerchant && (
                    <td className="px-4 py-3 text-xs">
                      {w.lastEdit ? (
                        <button type="button" onClick={() => setHistory(w)} className="text-left group">
                          <div className="text-slate-700 group-hover:text-primary group-hover:underline">
                            {w.lastEdit.by ?? 'Unknown'}
                            {/* A merchant changing their own address is a different event from one of
                                ours doing it, and the difference is the first thing asked in a dispute. */}
                            {w.lastEdit.type === 'merchant' && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700">Merchant</span>}
                          </div>
                          <div className="text-slate-400">{new Date(w.lastEdit.at).toLocaleString('en-IN')}</div>
                        </button>
                      ) : (
                        <span className="text-slate-300">Not edited yet</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {/* A MASTER location has no merchant, and the form posts under a merchant, so it
                          is managed in Settings - offering an Edit here that cannot save would be worse
                          than not offering one. */}
                      {w.merchantId ? (
                        <button type="button" onClick={() => setEditing(w)}
                          className="p-1.5 inline-flex text-slate-400 hover:text-primary hover:bg-primary/10 rounded" title="Edit this pickup location">
                          <Edit2 size={14} />
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400" title="Master locations are managed in Settings - Merchant Pickup Locations">Settings</span>
                      )}
                      {!isMerchant && (
                        <button type="button" onClick={() => setHistory(w)}
                          className="p-1.5 inline-flex text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" title="Edit history">
                          <History size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100">
          <span className="text-xs text-slate-500">
            {total.toLocaleString('en-IN')} pickup location{total === 1 ? '' : 's'}
          </span>
          <GmailPagination page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
        </div>
      </div>
    </div>
  );
}
